import { useAppDispatch, useAppSelector } from 'app/store';
import { getPlayerHeight } from 'app/store/AudioPlayer/selectors';
import { ReactComponent as SendSolidIcon } from 'resources/images/send-solid.svg';
import { ReactComponent as CloseIcon } from 'resources/images/close.svg';
import { Flex } from '@chakra-ui/layout';
import {
  Box,
  Card,
  Checkbox,
  IconButton,
  Input,
  VStack,
} from '@chakra-ui/react';
import { useResizableBlock } from 'app/utils/hooks/useResizableBlock';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { getCoWriterBarSettings } from 'app/store/Settings/selectors';
import { setCoWriterBarSettings } from 'app/store/Settings/slice';
import { useEffect, useState } from 'react';
import { fetchLyricsInspirationSearchEffect } from 'app/store/Subscription/effects';
import { isFeatureEnabled } from 'featureFlags';
import { getStyles } from './styles';
import { HEADER_HEIGHT } from '../HeaderBar';
import { CoWriterSelect } from './CoWriterSelect';
import { ResultList } from './ResultList';

interface FormValues {
  input: string;
  useLargeModel: boolean;
}

export const CoWriterBar = () => {
  const dispatch = useAppDispatch();
  const playerHeight = useAppSelector(getPlayerHeight);
  const coWriterBar = useAppSelector(getCoWriterBarSettings);
  const height = `calc(100vh - ${HEADER_HEIGHT}px - ${playerHeight}px)`;

  const {
    panelRef,
    cursor,
    handleMouseDown,
    isDragging,
    forceCompactMode,
  } = useResizableBlock('co-writer-bar', 'rtl', false);

  const {
    rootSx,
    wrapperSx,
    contentSx,
    cardSx,
    sidebarResizeLineSx,
  } = getStyles({
    forceCompactMode,
    isDragging,
  });

  const {
    register,
    reset,
    handleSubmit,
    formState: { isValid, isDirty, errors },
  } = useForm<FormValues>({
    defaultValues: { input: '', useLargeModel: false },
    mode: 'all',
    resolver: yupResolver(
      yup.object({
        input: yup
          .string()
          .trim()
          .required('')
          .matches(/^[\p{L}0-9]+$/u, {
            message:
              'Please only search one word, exciting updates are on the way soon!',
          }),
      })
    ),
  });

  const [list, setList] = useState<string[]>([]);
  const [state, setState] = useState<
    'loading' | 'error' | 'success' | 'empty' | 'noEntry'
  >('noEntry');

  const fetchSuggestions = async ({
    input: word,
    useLargeModel,
  }: FormValues) => {
    if (!word || !coWriterBar) {
      return;
    }

    setState('loading');
    try {
      const words = await dispatch(
        fetchLyricsInspirationSearchEffect({
          word,
          useLargeModel,
          ...coWriterBar,
        })
      ).unwrap();
      setList(words);
      setState(words.length > 0 ? 'success' : 'empty');
    } catch (e) {
      setState('success');
    }
  };

  useEffect(() => {
    setList([]);
    setState('noEntry');
    reset();
  }, [reset, coWriterBar?.type]);

  if (!coWriterBar) {
    return null;
  }

  const showSelectAIModel = isFeatureEnabled('selectAIModel');

  return (
    <Flex ref={panelRef} sx={rootSx}>
      <Flex sx={wrapperSx}>
        <Box
          onMouseDown={handleMouseDown}
          cursor={forceCompactMode ? 'default' : cursor}
          sx={sidebarResizeLineSx}
        >
          <Box />
        </Box>
        <VStack sx={contentSx} height={height} spacing={1}>
          <Card
            sx={cardSx}
            flex={1}
            overflow="hidden"
            data-testid="right-bar-card"
            as="form"
            onSubmit={handleSubmit(fetchSuggestions)}
          >
            <Flex
              gap={1}
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
            >
              <Flex flex={1} alignItems="center" justifyContent="space-between">
                <CoWriterSelect variant="text" />
                <IconButton
                  h={5}
                  w={5}
                  minW={5}
                  _hover={{
                    color: 'textBase',
                  }}
                  variant="text"
                  color="iconBase"
                  icon={<CloseIcon />}
                  aria-label="close-ai-feature"
                  onClick={() => {
                    dispatch(setCoWriterBarSettings());
                  }}
                />
              </Flex>
            </Flex>
            {showSelectAIModel && (
              <Checkbox mt={1} p={0} {...register('useLargeModel')}>
                Use Large Model
              </Checkbox>
            )}
            <ResultList
              errorMessage={errors.input?.message}
              list={list}
              state={state}
            />
            <Flex gap={2} mt={2}>
              <Input
                {...register('input')}
                height={10}
                bg="M.20"
                borderColor="M.30"
                _hover={{}}
              />
              <IconButton
                type="submit"
                size="md"
                rounded="full"
                bg="M.70"
                color="M.10"
                icon={<SendSolidIcon width={16} height={16} />}
                aria-label="Submit"
                _disabled={{
                  cursor: 'default',
                  opacity: 0.36,
                }}
                isDisabled={!isDirty || !isValid}
              />
            </Flex>
          </Card>
        </VStack>
      </Flex>
    </Flex>
  );
};
